import React, { useMemo } from 'react';
import type { CommunityTrendingTopic } from '../../types';
import './CommunityRightAside.css';

interface CommunityRightAsideProps {
  trendingTopics: CommunityTrendingTopic[];
  trendingStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  trendingError: string | null;
  onRefreshTrending: () => void;
  onNavigateToTopic?: (topicId: string) => void;
  recommendedCreators: RecommendedCreator[];
  onToggleFollow?: (authorId: number, isFollowing: boolean) => void;
  followPendingMap?: Record<number, boolean>;
  onOpenCreator?: (authorId: number) => void;
}

export interface RecommendedCreator {
  id: number;
  name: string;
  username?: string | null;
  avatar_url?: string | null;
  posts: number;
  likes: number;
  comments: number;
  followsAuthor: boolean;
  topTopics: string[];
  latestTitle?: string;
  latestCreatedAt?: string | null;
}

const CommunityRightAside: React.FC<CommunityRightAsideProps> = ({
  trendingTopics,
  trendingStatus,
  trendingError,
  onRefreshTrending,
  onNavigateToTopic,
  recommendedCreators,
  onToggleFollow,
  followPendingMap,
  onOpenCreator,
}) => {
  const maxMentions = useMemo(() => {
    return trendingTopics.reduce((max, item) => Math.max(max, item.mentions), 0);
  }, [trendingTopics]);

  return (
    <aside className="community-shell__right">
      <section className="community-shell__card">
        <header>
          <h3>热门话题</h3>
          <button type="button" onClick={onRefreshTrending} disabled={trendingStatus === 'loading'}>
            刷新
          </button>
        </header>
        {trendingStatus === 'loading' && <div className="community-shell__placeholder">热门话题加载中...</div>}
        {trendingStatus === 'failed' && trendingError && (
          <div className="community-shell__error">{trendingError}</div>
        )}
        {trendingStatus === 'succeeded' && trendingTopics.length === 0 && (
          <div className="community-shell__placeholder">暂无热门话题，快来发布第一篇吧</div>
        )}
        {trendingTopics.length > 0 && (
          <ul className="community-shell__trending">
            {trendingTopics.map((topic, index) => {
              const ratio = maxMentions > 0 ? topic.mentions / maxMentions : 0;
              return (
                <li key={topic.id}>
                  <div className="community-shell__trending-rank">#{index + 1}</div>
                  <div className="community-shell__trending-info">
                    <button
                      type="button"
                      onClick={() => onNavigateToTopic?.(topic.id)}
                      className="community-shell__trending-link"
                    >
                      {topic.label}
                    </button>
                    <div className="community-shell__trending-meter">
                      <span style={{ width: `${Math.max(0.18, ratio) * 100}%` }} />
                    </div>
                    <small>{topic.mentions} 次讨论</small>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="community-shell__card">
        <header>
          <h3>可能感兴趣</h3>
        </header>
        {recommendedCreators.length === 0 ? (
          <div className="community-shell__placeholder">
            关注一些帖子后，我们会为你推荐社区创作者。
          </div>
        ) : (
          <ul className="community-shell__creators">
            {recommendedCreators.map((creator) => {
              const pending = followPendingMap?.[creator.id] ?? false;
              return (
                <li key={creator.id}>
                  <div
                    className="community-shell__creator-header"
                    role={onOpenCreator ? 'button' : undefined}
                    tabIndex={onOpenCreator ? 0 : undefined}
                    onClick={onOpenCreator ? () => onOpenCreator(creator.id) : undefined}
                    onKeyDown={
                      onOpenCreator
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              onOpenCreator(creator.id);
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="community-shell__creator-avatar">
                      {creator.avatar_url ? (
                        <img src={creator.avatar_url} alt={creator.name} />
                      ) : (
                        <span>{creator.name.slice(0, 1)}</span>
                      )}
                    </div>
                    <div className="community-shell__creator-meta">
                      <strong>{creator.name}</strong>
                      {creator.username ? <span>@{creator.username}</span> : null}
                    </div>
                    {onToggleFollow && (
                      <button
                        type="button"
                        className={`community-shell__creator-follow${creator.followsAuthor ? ' community-shell__creator-follow--active' : ''}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleFollow(creator.id, creator.followsAuthor);
                        }}
                        disabled={pending}
                      >
                        {pending ? '处理中…' : creator.followsAuthor ? '已关注' : '关注'}
                      </button>
                    )}
                  </div>
                  <div className="community-shell__creator-stats">
                    <span>帖子 {creator.posts}</span>
                    <span>赞 {creator.likes}</span>
                    <span>互动 {creator.comments}</span>
                  </div>
                  {creator.topTopics.length > 0 && (
                    <div className="community-shell__creator-topics">
                      {creator.topTopics.map((topic) => (
                        <span key={topic}>#{topic}</span>
                      ))}
                    </div>
                  )}
                  {creator.latestTitle && (
                    <p className="community-shell__creator-latest">最新：{creator.latestTitle}</p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </aside>
  );
};

export default CommunityRightAside;
